<template>
    <div class="parking-info-window">
        <div class="parking-info-window__header">
            <span class="parking-info-window__title">
                {{ headerTitle }}
            </span>
        </div>
        <div class="parking-info-window__body">
            <div class="parking-info-window__section-label">
                {{ sectionLabel }}
            </div>

            <div v-if="lot.active != null" class="parking-info-window__row">
                <span class="parking-info-window__label">{{ $t('Active') }}:</span>
                <span
                    class="parking-info-window__value"
                    :class="lot.active ? 'parking-info-window__value--yes' : 'parking-info-window__value--no'"
                >
                    {{ lot.active ? $t('Yes') : $t('No') }}
                </span>
            </div>

            <template v-if="!isPlanned">
                <div class="parking-info-window__row">
                    <span class="parking-info-window__label">{{ $t('Height') }}:</span>
                    <span class="parking-info-window__value">{{ heightDisplay }}</span>
                </div>
            </template>

            <div v-if="lot.created" class="parking-info-window__row">
                <span class="parking-info-window__label">{{ $t('Created') }}:</span>
                <span class="parking-info-window__value">{{ formatDateTime(lot.created) }}</span>
            </div>
            <div v-if="lot.last_changed" class="parking-info-window__row">
                <span class="parking-info-window__label">{{ $t('Last changed') }}:</span>
                <span class="parking-info-window__value">{{ formatDateTime(lot.last_changed) }}</span>
            </div>
            <div v-if="lot.changed_by" class="parking-info-window__row">
                <span class="parking-info-window__label">{{ $t('Changed by') }}:</span>
                <span class="parking-info-window__value">{{ lot.changed_by }}</span>
            </div>

            <div class="parking-info-window__row">
                <span class="parking-info-window__label">{{ borderLabel }}:</span>
                <span class="parking-info-window__value">{{ borderValue }}</span>
            </div>
            <div class="parking-info-window__row">
                <span class="parking-info-window__label">{{ pathLabel }}:</span>
                <span class="parking-info-window__value">{{ pathValue }}</span>
            </div>

            <template v-if="!isPlanned">
                <div
                    v-for="(border, idx) in borders"
                    :key="`border-${idx}`"
                    class="parking-info-window__detail-block parking-info-window__detail-block--border"
                >
                    <div class="parking-info-window__detail-title">
                        {{ $t('Border') }} {{ idx + 1 }} {{ $t('of') }} {{ borders.length }}
                    </div>
                    <div class="parking-info-window__detail-rows">
                        <div class="parking-info-window__row parking-info-window__row--compact">
                            <span class="parking-info-window__label">{{ $t('Driven') }}:</span>
                            <span
                                class="parking-info-window__value"
                                :class="boolClass(border.driven)"
                            >
                                {{ boolLabel(border.driven) }}
                            </span>
                        </div>
                        <div v-if="border.changed_by" class="parking-info-window__row parking-info-window__row--compact">
                            <span class="parking-info-window__label">{{ $t('Changed by') }}:</span>
                            <span class="parking-info-window__value">{{ border.changed_by }}</span>
                        </div>
                        <div v-if="border.valid_from" class="parking-info-window__row parking-info-window__row--compact">
                            <span class="parking-info-window__label">{{ $t('Valid From') }}:</span>
                            <span class="parking-info-window__value">{{ formatDateTime(border.valid_from) }}</span>
                        </div>
                        <div v-if="border.valid_to" class="parking-info-window__row parking-info-window__row--compact">
                            <span class="parking-info-window__label">{{ $t('Valid To') }}:</span>
                            <span class="parking-info-window__value">{{ formatDateTime(border.valid_to) }}</span>
                        </div>
                        <div class="parking-info-window__row parking-info-window__row--compact">
                            <span class="parking-info-window__label">{{ $t('Geometry type') }}:</span>
                            <span class="parking-info-window__value">{{ geometrySummary(border.geometry) }}</span>
                        </div>
                    </div>
                </div>

                <div
                    v-for="(pathItem, idx) in paths"
                    :key="`path-${idx}`"
                    class="parking-info-window__detail-block parking-info-window__detail-block--path"
                >
                    <div class="parking-info-window__detail-title">
                        {{ $t('Path') }} {{ idx + 1 }} {{ $t('of') }} {{ paths.length }}
                    </div>
                    <div class="parking-info-window__detail-rows">
                        <div class="parking-info-window__row parking-info-window__row--compact">
                            <span class="parking-info-window__label">{{ $t('Driven') }}:</span>
                            <span
                                class="parking-info-window__value"
                                :class="boolClass(pathItem.driven)"
                            >
                                {{ boolLabel(pathItem.driven) }}
                            </span>
                        </div>
                        <div v-if="pathItem.changed_by" class="parking-info-window__row parking-info-window__row--compact">
                            <span class="parking-info-window__label">{{ $t('Changed by') }}:</span>
                            <span class="parking-info-window__value">{{ pathItem.changed_by }}</span>
                        </div>
                        <div v-if="pathItem.valid_from" class="parking-info-window__row parking-info-window__row--compact">
                            <span class="parking-info-window__label">{{ $t('Valid From') }}:</span>
                            <span class="parking-info-window__value">{{ formatDateTime(pathItem.valid_from) }}</span>
                        </div>
                        <div v-if="pathItem.valid_to" class="parking-info-window__row parking-info-window__row--compact">
                            <span class="parking-info-window__label">{{ $t('Valid To') }}:</span>
                            <span class="parking-info-window__value">{{ formatDateTime(pathItem.valid_to) }}</span>
                        </div>
                        <div class="parking-info-window__row parking-info-window__row--compact">
                            <span class="parking-info-window__label">{{ $t('Geometry type') }}:</span>
                            <span class="parking-info-window__value">{{ geometrySummary(pathItem.geometry) }}</span>
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { formatDate } from '@/helpers/dateHelper'

export default {
    name: 'ParkingLotInfoWindow',
    props: {
        lot: {
            type: Object,
            required: true,
        },
    },
    computed: {
        isPlanned() {
            return this.lot.isPlannedSlot === true
        },
        borders() {
            if (this.isPlanned) {
                return this.lot.border?.coordinates ? [this.lot.border] : []
            }
            return this.lot.borders || []
        },
        paths() {
            if (this.isPlanned) {
                return this.lot.path?.coordinates ? [this.lot.path] : []
            }
            return this.lot.paths || []
        },
        headerTitle() {
            const name = this.lot.name?.trim()
            return name || this.$t('Unnamed')
        },
        sectionLabel() {
            return this.isPlanned ? this.$t('Parking area') : this.$t('Parking position')
        },
        heightDisplay() {
            const value = this.lot.implement_height
            if (value == null || value === '') return 'N/A'
            return `${value} m`
        },
        borderLabel() {
            return this.isPlanned ? this.$t('Border') : this.$t('Borders (count)')
        },
        borderValue() {
            if (this.isPlanned) {
                return this.borders.length ? this.$t('Yes') : this.$t('No')
            }
            return String(this.borders.length)
        },
        pathLabel() {
            return this.isPlanned ? this.$t('Path') : this.$t('Paths (count)')
        },
        pathValue() {
            if (this.isPlanned) {
                return this.paths.length ? this.$t('Yes') : this.$t('No')
            }
            return String(this.paths.length)
        },
    },
    methods: {
        formatDateTime(value) {
            const d = formatDate(value)
            return `${d.date} ${d.time}`
        },
        geometrySummary(geometry) {
            return geometry?.type || '—'
        },
        boolLabel(value) {
            if (value == null) return 'N/A'
            return value ? this.$t('Yes') : this.$t('No')
        },
        boolClass(value) {
            if (value == null) return ''
            return value ? 'parking-info-window__value--yes' : 'parking-info-window__value--no'
        },
    },
}
</script>

<style scoped>
.parking-info-window {
    padding: 12px;
    min-width: 260px;
    max-width: 420px;
    max-height: 70vh;
    overflow-y: auto;
    font-family: Arial, sans-serif;
}

.parking-info-window__header {
    margin-bottom: 12px;
    border-bottom: 1px solid #e0e0e0;
    padding-bottom: 8px;
}

.parking-info-window__title {
    font-weight: 600;
    color: #2c3e50;
    font-size: 16px;
}

.parking-info-window__body {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.parking-info-window__section-label {
    font-weight: 600;
    color: #1a5276;
    margin-bottom: 2px;
    text-align: center;
}

.parking-info-window__row {
    display: block;
}

.parking-info-window__row--compact {
    font-size: 13px;
}

.parking-info-window__label {
    font-weight: 600;
    color: #2c3e50;
}

.parking-info-window__value {
    margin-left: 8px;
    color: #34495e;
}

.parking-info-window__value--yes {
    color: #27ae60;
}

.parking-info-window__value--no {
    color: #e74c3c;
}

.parking-info-window__detail-block {
    margin-top: 10px;
    padding-top: 10px;
    border-top: 1px solid #e0e0e0;
}

.parking-info-window__detail-title {
    font-weight: 600;
    margin-bottom: 6px;
    text-align: center;
}

.parking-info-window__detail-block--border .parking-info-window__detail-title {
    color: #5EA529;
}

.parking-info-window__detail-block--path .parking-info-window__detail-title {
    color: #27B5FF;
}

.parking-info-window__detail-rows {
    display: flex;
    flex-direction: column;
    gap: 4px;
}
</style>
